// @ts-ignore
import {
  TruexAdRendererCTV,
  TruexAdRendererParameters,
} from '@truex/ad-renderer';
import { AppData, Lightning, Registry } from '@lightningjs/sdk';
import {
  InteractiveAdCompleteType,
  InteractiveAdData,
} from './InteractiveAdUtils';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';

// Reference: https://github.com/socialvibe/truex-ad-renderer-web-integration/blob/develop/DOCS.md
export default class InteractiveAdManager extends Lightning.Component {
  adRenderer: any;
  adOverlay: any;
  adData?: InteractiveAdData;
  adCredit = false;

  isInitializing = false;

  public start = async (adData: InteractiveAdData) => {
    if (this.isInitializing) return;
    this.reset();
    this.isInitializing = true;

    this.adData = adData;

    try {
      const options = {
        supportsUserCancelStream: true, // Required for userCancelStream event
      };

      // One TAR (TrueX Ad Renderer) instance per ad from documentation
      const renderer = new TruexAdRendererCTV(this.adData.vastConfig, options);
      renderer.subscribe(this.handleAdEvent.bind(this));
      this.adRenderer = renderer;

      const vast = await renderer.init();
      this.adOverlay = await renderer.start(vast);
    } catch (e) {
      this.handleAdError(e);
    }

    this.isInitializing = false;
  };

  override _inactive() {
    this.reset();
  }

  get isInteractiveAdPlaying() {
    return !!this.adOverlay || !!this.adRenderer;
  }

  reset() {
    this.isInitializing = false;
    this.adCredit = false;
    this.adData = undefined;

    this.cleanupRenderer();
    this.cleanupAdOverlay();
  }

  handleAdEvent(event: any) {
    const adEvents = this.adRenderer.adEvents;
    switch (event.type) {
      case adEvents.adError:
        this.handleAdError(event.errorMessage);
        break;
      case adEvents.adStarted:
        this.signal('interactiveAdStarted', this.adData);
        break;
      case adEvents.adFreePod:
        // Usually 30 seconds and an interaction.  Fires during interaction experience and before exiting the ad
        this.adCredit = true;
        break;
      case adEvents.userCancelStream:
        // User backed out of the choice card, which means backing out of the entire video.
        this.exitPlayback();
        break;
      case adEvents.noAdsAvailable:
        this.restorePlayback(InteractiveAdCompleteType.NO_ADS_AVAILABLE);
        break;
      case adEvents.adCompleted: // Opt out (automatic and manual) and exit credited ad
        // IDVx does not fire adFreePod event. Use custom handling
        if (this.adData?.adType === 'IDVx') this.adCredit = event.timeSpent > 0;

        this.restorePlayback(InteractiveAdCompleteType.COMPLETE);
        break;
    }
  }

  handleAdError(error: any) {
    if (!this.adData) return;

    reportError({
      error: error instanceof Error ? error : new Error(error),
      userFacing: false,
      code: ErrorCode.c45,
    });
    this.restorePlayback(InteractiveAdCompleteType.ERROR);
  }

  cleanupAdOverlay() {
    if (this.adOverlay instanceof HTMLElement) {
      (this.adOverlay as HTMLElement).remove();
    }
    this.adOverlay = null;
  }

  cleanupRenderer() {
    if (this.adRenderer) {
      // Ensure the ad is no longer blocking back or key events, etc.
      this.adRenderer.stop();
    }
    this.adRenderer = null;
  }

  restorePlayback(adCompleteType: InteractiveAdCompleteType) {
    const credit = this.adCredit;
    const adData = this.adData;

    this.reset();

    this.signal('interactiveAdEnd', {
      adCredit: credit,
      interactiveAdData: adData,
      adCompleteType,
    });
  }

  exitPlayback() {
    const adData = this.adData;
    const exitDelay = AppData!.device.getInteractiveAdExitDelay();
    this.reset();

    // Unclear why this works. Suspect TAR.stop() or similar needs to finish as they
    // have some documented back blocking behaviors.  TAR.stop() does not support await.
    Registry.setTimeout(() => {
      this.signal('interactiveAdExit', {
        interactiveAdData: adData,
      });
    }, exitDelay);
  }
}
